import {
  Avatar,
  Button,
  Divider,
  Drawer,
  Group,
  Menu,
  NavLink,
  Stack,
  UnstyledButton,
} from "@mantine/core";
import {
  IconChevronDown,
  IconCreditCard,
  IconLayoutGrid,
  IconLogout,
  IconMenu2,
  IconSettings,
  IconSparkles,
  IconUsersGroup,
} from "@tabler/icons-react";
import { type ReactNode, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router";
import { useAuth } from "../auth/provider";
import { useMessages } from "../i18n/provider";
import styles from "./app-frame.module.css";
import { LanguageSwitcher } from "./language-switcher";
import { Logo } from "./logo";
import { ThemeToggle } from "./theme-toggle";

interface NavItem {
  to: string;
  label: string;
  icon: ReactNode;
}

export function AppFrame({ children, active }: { children: ReactNode; active?: string }) {
  const t = useMessages();
  const { me, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [opened, setOpened] = useState(false);

  const items: NavItem[] = [
    {
      to: "/dashboard",
      label: t("nav.dashboard"),
      icon: <IconLayoutGrid size={18} stroke={1.5} />,
    },
    {
      to: "/templates",
      label: t("nav.templates"),
      icon: <IconSparkles size={18} stroke={1.5} />,
    },
    {
      to: "/organization",
      label: t("nav.organizations"),
      icon: <IconUsersGroup size={18} stroke={1.5} />,
    },
    {
      to: "/billing",
      label: t("nav.billing"),
      icon: <IconCreditCard size={18} stroke={1.5} />,
    },
    {
      to: "/settings",
      label: t("nav.settings"),
      icon: <IconSettings size={18} stroke={1.5} />,
    },
  ];

  const current =
    active ??
    items.find(
      (item) => location.pathname === item.to || location.pathname.startsWith(`${item.to}/`),
    )?.to;

  const handleSignOut = async () => {
    await signOut();
    navigate("/");
  };

  const nav = (
    <Stack gap={2}>
      {items.map((item) => (
        <NavLink
          key={item.to}
          component={Link}
          to={item.to}
          label={item.label}
          leftSection={item.icon}
          active={current === item.to}
          onClick={() => setOpened(false)}
          className={styles.navLink}
        />
      ))}
    </Stack>
  );

  return (
    <div className={styles.shell}>
      <header className={styles.header}>
        <div className={styles.headerInner}>
          <Logo to="/dashboard" />
          <nav className={styles.desktopNav} aria-label="Main">
            {items.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className={styles.navItem}
                data-active={current === item.to ? "true" : undefined}
              >
                {item.label}
              </Link>
            ))}
          </nav>
          <Group gap={4} className={styles.actions}>
            <ThemeToggle />
            <LanguageSwitcher compact />
            <Menu position="bottom-end" width={200} shadow="md">
              <Menu.Target>
                <UnstyledButton className={styles.avatarButton} aria-label={t("nav.accountMenu")}>
                  <Avatar size={30} radius="xl" src={me?.image ?? undefined}>
                    {(me?.name ?? me?.email ?? "?").slice(0, 1).toUpperCase()}
                  </Avatar>
                  <IconChevronDown size={14} stroke={1.5} />
                </UnstyledButton>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Label>{me?.email}</Menu.Label>
                <Menu.Item
                  component={Link}
                  to="/settings"
                  leftSection={<IconSettings size={16} stroke={1.5} />}
                >
                  {t("nav.settings")}
                </Menu.Item>
                <Menu.Item
                  component={Link}
                  to="/billing"
                  leftSection={<IconCreditCard size={16} stroke={1.5} />}
                >
                  {t("nav.billing")}
                </Menu.Item>
                <Menu.Divider />
                <Menu.Item
                  color="red"
                  leftSection={<IconLogout size={16} stroke={1.5} />}
                  onClick={() => void handleSignOut()}
                >
                  {t("nav.signOut")}
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
            <Button
              className={styles.menuButton}
              variant="subtle"
              color="gray"
              size="compact-md"
              aria-label="Menu"
              onClick={() => setOpened(true)}
            >
              <IconMenu2 size={20} stroke={1.5} />
            </Button>
          </Group>
        </div>
      </header>

      <main className={styles.main}>{children}</main>

      <Drawer
        opened={opened}
        onClose={() => setOpened(false)}
        position="right"
        size="xs"
        title={<Logo to="/dashboard" />}
      >
        {nav}
        <Divider my="md" />
        <Button
          variant="subtle"
          color="gray"
          justify="flex-start"
          leftSection={<IconLogout size={18} stroke={1.5} />}
          onClick={() => void handleSignOut()}
        >
          {t("nav.signOut")}
        </Button>
      </Drawer>
    </div>
  );
}
